import { useState, useEffect, useCallback } from 'react';
import { useAuth } from "auth/useAuth";
import { ApiService } from '../';
import { isProfileComplete, getMissingProfileFields } from '../utils/profileUtils';
import { setAuthToken } from '../../helpers/api_helper';
import eventBus from '../utils/eventBus';

/**
 * Whether the signed-in user has a complete profile (see profileUtils).
 * Re-checks when the app emits 'profile-updated'.
 * @returns {{ hasProfile: boolean, missingFields: string[], isLoading: boolean, error: string|null, refreshProfile: () => void }}
 */
export const useProfileCheck = () => {
  const auth = useAuth();
  const [hasProfile, setHasProfile] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const checkProfile = async () => {
      try {
        setIsLoading(true);
        if (auth.user?.id_token) setAuthToken(auth.user.id_token);
        const profile = await ApiService.getUserProfile();
        if (cancelled) return;
        setHasProfile(isProfileComplete(profile));
        setMissingFields(getMissingProfileFields(profile));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setHasProfile(false);
          setMissingFields(getMissingProfileFields(null));
          setError(null);
        } else {
          // Network/auth hiccup: keep the previous answer, just record the error.
          console.error('Profile check failed:', err);
          setError('Não foi possível verificar seu perfil.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (auth.isAuthenticated && auth.user?.id_token) {
      checkProfile();
    } else {
      setHasProfile(false);
      setIsLoading(false);
    }

    const handleProfileUpdate = () => setRefreshCounter((n) => n + 1);
    eventBus.on('profile-updated', handleProfileUpdate);
    return () => {
      cancelled = true;
      eventBus.off('profile-updated', handleProfileUpdate);
    };
  }, [refreshCounter, auth.isAuthenticated, auth.user?.id_token]);

  const refreshProfile = useCallback(() => setRefreshCounter((n) => n + 1), []);

  return { hasProfile, missingFields, isLoading, error, refreshProfile };
};
